import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EmployerForm from "@/components/EmployerForm";
import CandidateForm from "@/components/CandidateForm";
import { api } from "@/lib/api";
import { ArrowLeft, MapPin, Sparkles } from "lucide-react";

function fmtEUR(n) {
  return new Intl.NumberFormat("en-EU", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
  }).format(n);
}

export default function CountryPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/countries/${slug}`).then(({ data }) => {
      if (active) { setData(data); setLoading(false); }
    }).catch((e) => {
      if (active) { setError("Country not found"); setLoading(false); }
    });
    return () => { active = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-32 text-slate-500">Loading…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="font-display font-black text-4xl text-slate-900">Country not found</h1>
          <p className="mt-3 text-slate-600">We don't have a page for "{slug}" yet.</p>
          <Link to="/" className="mt-6 inline-block text-blue-600">← Back to home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const sal = data.salary_preview_eur || {};

  return (
    <div data-testid={`country-page-${slug}`} className="bg-slate-50 min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <img
          src="https://images.unsplash.com/photo-1623588958378-660cae3dd1d3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
          alt={`${data.name} tech hub`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/70 to-slate-900/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-white">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 text-xs font-mono-ui uppercase tracking-[0.2em]">
            <Sparkles className="w-3.5 h-3.5 text-blue-300" />
            European tech market · 2025
          </div>
          <h1 data-testid="country-title" className="mt-5 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            <span className="mr-3">{data.flag}</span>
            Recruitment in {data.name}
          </h1>
          <p className="mt-4 text-lg text-slate-200 max-w-2xl">{data.tagline}</p>

          <div className="mt-8 flex flex-wrap gap-2">
            {data.hubs.map((h) => (
              <span key={h} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs">
                <MapPin className="w-3.5 h-3.5" /> {h}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Strengths + Salary */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Strengths</div>
            <h2 className="font-display font-bold mt-3 text-3xl tracking-tight text-slate-900">
              What {data.name} delivers
            </h2>
            <ul className="mt-6 grid grid-cols-2 gap-3">
              {data.strengths.map((s) => (
                <li key={s} className="rounded-xl border border-slate-200 bg-white p-4 text-slate-800 font-display font-semibold">
                  {s}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">2025 Salary Preview</div>
            <h2 className="font-display font-bold mt-3 text-3xl tracking-tight text-slate-900">
              Gross annual median (EUR)
            </h2>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden">
              {["junior", "mid", "senior", "lead"].map((k, i) => (
                <div key={k} className={`flex items-center justify-between px-5 py-4 ${i ? "border-t border-slate-100" : ""}`}>
                  <span className="text-slate-600 capitalize">{k}</span>
                  <span className="font-display font-bold text-slate-900">{sal[k] ? fmtEUR(sal[k]) : "—"}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">
              For Software Engineer baseline. Use the Salary Benchmark tool on the home page for role-specific data.
            </p>
          </div>
        </div>
      </section>

      {/* Dual CTA on country page */}
      <section className="py-16 lg:py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold">Hire in {data.name}</div>
            <h3 className="font-display font-bold mt-3 text-3xl text-slate-900">
              Get a {data.name} shortlist
            </h3>
            <p className="mt-3 text-slate-600">
              Submit your role — get AI-ranked, recruiter-vetted candidates in 5 working days.
            </p>
            <div className="mt-6"><EmployerForm /></div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-teal-700 font-semibold">Work in {data.name}</div>
            <h3 className="font-display font-bold mt-3 text-3xl text-slate-900">
              Apply for {data.name} roles
            </h3>
            <p className="mt-3 text-slate-600">
              Upload your CV — get matched with open roles in {data.name} and across Europe.
            </p>
            <div className="mt-6"><CandidateForm /></div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
